import Commons from 'js/helpers/Commons';
import RapidSearchConstant from 'js/constants/rapidSearch'
import { isAU } from '../../constants/crux';

const { minAndMaxToRange } = Commons;
const commaSeparatedToPipe = (value) => value?.split(',')?.join('|');

const area = (value) => minAndMaxToRange({
    min: value.min && encodeURIComponent(`${value.min}${value.unit}`),
    max: value.max && encodeURIComponent(`${value.max}${value.unit}`),
});

const asIs = value => !!value && encodeURIComponent(value);

// Regex is used to split values using comma without
// affecting the possible values that has comma inside
// i.e '"test, test", "test"' - first value is 'test, test', second value is 'test'
const splitValuesWrappedWithQuotes = value => value
    .match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)
    ?.map(e => e.replace(/"/g, ''))
    ?.join('|');

const listToPipeSeparated = value => value?.join('|');

const handleAverageHold = (value, filters) => {
    const typeExists = (type) => {
        const typeList = filters?.type ? filters?.type?.split(',') : [];
        return !typeList.length
            || (typeList.length && typeList.find(e => e.toLowerCase() === type));
    }
    const holdPeriodHouse = value.holdPeriodHouse ? minAndMaxToRange(value.holdPeriodHouse) : '';

    const holdPeriod = [];
    const houseResidentialExists = ['house', 'residential'].some(type => typeExists(type));
    holdPeriod.push(holdPeriodHouse && houseResidentialExists ? holdPeriodHouse : '');

    if (isAU) {
        const holdPeriodUnit = value.holdPeriodUnit ? minAndMaxToRange(value.holdPeriodUnit) : '';
        holdPeriod.push(holdPeriodUnit && typeExists('unit') ? holdPeriodUnit : '');
    }

    return holdPeriod.some(Boolean) ? holdPeriod.join('|') : '';
};

const sort = (values) => {
    const sortMap = {
        [RapidSearchConstant.ASCENDING_SORT]: 'asc',
        [RapidSearchConstant.DESCENDING_SORT]: 'desc',
    };
    return values.map((value) => `${value.attr}-${sortMap[value.order]}`).join('|');
};

const SearchFilterEncoder = {
    radius: asIs,
    beds: minAndMaxToRange,
    baths: minAndMaxToRange,
    carSpaces: minAndMaxToRange,
    salesLastSoldPrice: minAndMaxToRange,
    salesLastSaleContractDate: minAndMaxToRange,
    salesLastCampaignLastListedPrice: minAndMaxToRange,
    rentalLastCampaignLastListedPrice: minAndMaxToRange,
    salesLastCampaignEndDate: minAndMaxToRange,
    rentalLastCampaignEndDate: minAndMaxToRange,
    salesLastCampaignStartDate: minAndMaxToRange,
    rentalLastCampaignStartDate: minAndMaxToRange,
    isActiveForSaleCampaign: asIs,
    isActiveForRentCampaign: asIs,
    type: commaSeparatedToPipe,
    salesLastCampaignListedType: commaSeparatedToPipe,
    landArea: area,
    floorArea: value => area({ ...value, unit: 'm²' }),
    yearBuilt: minAndMaxToRange,
    salesLastSaleSettlementDate: minAndMaxToRange,
    rentalLastCampaignLastListedPeriod: asIs,
    salesLastSaleIsArmsLength: asIs,
    salesLastSaleSource: () => true,
    salesLastCampaignDaysOnMarket: asIs,
    rentalOccupancy: asIs,
    subType: splitValuesWrappedWithQuotes,
    developmentZoneDescription: splitValuesWrappedWithQuotes,
    landUse: splitValuesWrappedWithQuotes,
    keywords: listToPipeSeparated,
    feeCode: asIs,
    factoryArea: area,
    mezzanineArea: area,
    officeShowroomArea: area,
    otherArea: area,
    shopArea: area,
    showroomArea: area,
    warehouseArea: area,
    workshopArea: area,
    distanceToHospital: minAndMaxToRange,
    distanceToRiverFrontage: minAndMaxToRange,
    distanceToSchool: minAndMaxToRange,
    distanceToSeaRiverCanal: minAndMaxToRange,
    distanceToShops: minAndMaxToRange,
    boatRampMooringSpace: asIs,
    fenced: asIs,
    grannyFlatSelfContained: asIs,
    lockupGarages: minAndMaxToRange,
    swimmingPool: asIs,
    treesOnLand: asIs,
    noOfTennisCourts: minAndMaxToRange,
    wallMaterial: asIs,
    materialsInExternalWalls: asIs,
    roofMaterial: asIs,
    materialsInRoof: asIs,
    canalFrontage: asIs,
    coastFrontage: asIs,
    golfFrontage: asIs,
    highExposure: asIs,
    lakeFrontage: asIs,
    riverFrontage: asIs,
    scenicView: asIs,
    waterFrontage: asIs,
    airConditioned: asIs,
    ductedHeating: asIs,
    builtInWardrobes: asIs,
    ductedVacuum: asIs,
    fireplace: asIs,
    openPlan: asIs,
    polishedFloors: asIs,
    renovated: asIs,
    diningRooms: minAndMaxToRange,
    familyRumpusRooms: minAndMaxToRange,
    floorLevelsInside: minAndMaxToRange,
    kitchens: minAndMaxToRange,
    loungeRooms: minAndMaxToRange,
    loungeDiningRoomsCombined: minAndMaxToRange,
    noOfStudyRooms: minAndMaxToRange,
    toilets: minAndMaxToRange,
    heritageOtherClassification: asIs,
    currentUnimprovedLandValue: minAndMaxToRange,
    unimprovedCapitalValue: minAndMaxToRange,
    unimprovedCapitalValueDate: minAndMaxToRange,
    valuationLandValue: minAndMaxToRange,
    valuationImprovementsValue: minAndMaxToRange,
    valuationCapitalValue: minAndMaxToRange,
    valuationAssessmentDate: minAndMaxToRange,
    allWeatherAccessToLand: asIs,
    allWeatherRoadsToFarm: asIs,
    permanentWater: asIs,
    sealedRoadsToProperty: asIs,
    housesOnFarm: minAndMaxToRange,
    irrigatedArea: minAndMaxToRange,
    damsBores: minAndMaxToRange,
    paddocksYards: minAndMaxToRange,
    accessAvailable24Hrs: asIs,
    accessSecurityInstalled: asIs,
    closeToPublicTransport: asIs,
    liftInstalled: asIs,
    onMainRoad: asIs,
    sewage: asIs,
    townSewerageAvailable: asIs,
    townWaterAvailable: asIs,
    water: asIs,
    buildingArea: area,
    equivalentBuildingArea: area,
    floorLevelOfUnit: minAndMaxToRange,
    floorLevelsInsideUnit: minAndMaxToRange,
    flats: minAndMaxToRange,
    streetFrontage: minAndMaxToRange,
    totalFloorsInBuilding: minAndMaxToRange,
    yearBuildingRefurbished: minAndMaxToRange,
    yearEstablished: minAndMaxToRange,
    freeStandingBuilding: asIs,
    statusType: asIs,
    tenureData: handleAverageHold,
    salesClassification: listToPipeSeparated,
    sort,
    suburbOnly: asIs,
    latestConsumerSaleEstimate: minAndMaxToRange,
    latestConsumerSaleConfidence: splitValuesWrappedWithQuotes,
}

export default SearchFilterEncoder;
